/**
 * Form "Nova ordem de entrega" (SlideOverPanel).
 *
 * Auditoria 2026-08-07: a logística era um beco sem saída — `criarOe` existia
 * no service e nenhuma tela o chamava, então as listas só sabiam exibir OEs
 * que ninguém conseguia criar. Normalmente a OE nasce sozinha quando a
 * produção carrega o ambiente; este form cobre o resto (entrega avulsa,
 * reentrega de um ambiente que não foi entregue, pedido importado).
 *
 * O AMBIENTE é a escolha que importa: é ele que define a fatia do pedido
 * usada no rateio da comissão e das parcelas ancoradas na entrega (D3).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiTruck } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { criarOe } from "../../../services/logistica";
import { listarPedidos, obterPedido } from "../../../services/pedidos";
import {
  ErrorMsg,
  Field,
  Input,
  Select,
  Textarea,
} from "../../Orcamentos/styles";

const SITUACOES_COM_ENTREGA = ["EM_PRODUCAO", "EM_EXPEDICAO", "RECEBIDO"];

export default function NovaOeForm({ aberto, onFechar, onCriada }) {
  const { request } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [pedidoId, setPedidoId] = useState("");
  const [detalhe, setDetalhe] = useState(null);
  const [ambienteId, setAmbienteId] = useState("");
  const [endereco, setEndereco] = useState("");
  const [telefone, setTelefone] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [erro, setErro] = useState(null);

  const carregarPedidos = useCallback(async () => {
    setErro(null);
    try {
      const todos = [];
      for (const situacao of SITUACOES_COM_ENTREGA) {
        const p = await listarPedidos(request, { situacao, pageSize: 200 });
        todos.push(...(Array.isArray(p) ? p : p?.results || []));
      }
      setPedidos(todos);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar os pedidos.");
    }
  }, [request]);

  useEffect(() => {
    if (aberto) carregarPedidos();
  }, [aberto, carregarPedidos]);

  // Ao escolher o pedido, busca os ambientes e sugere o endereço do cliente.
  useEffect(() => {
    let cancelado = false;
    if (!pedidoId) {
      setDetalhe(null);
      setAmbienteId("");
      return undefined;
    }
    (async () => {
      try {
        const p = await obterPedido(request, pedidoId);
        if (cancelado) return;
        setDetalhe(p);
        setAmbienteId("");
        if (!endereco) setEndereco(p?.cliente_endereco || "");
        if (!telefone) setTelefone(p?.cliente_telefone || "");
      } catch (err) {
        if (!cancelado) setErro(err?.message || "Erro ao carregar o pedido.");
      }
    })();
    return () => { cancelado = true; };
    // `endereco`/`telefone` fora das deps de propósito: são sugestões
    // iniciais e não devem refazer o fetch a cada tecla digitada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoId, request]);

  const ambientes = useMemo(() => detalhe?.ambientes || [], [detalhe]);

  const limpar = () => {
    setPedidoId(""); setDetalhe(null); setAmbienteId("");
    setEndereco(""); setTelefone(""); setObservacoes(""); setErro(null);
  };

  const salvar = async () => {
    setErro(null);
    // Validação por exceção: com `return` cedo, o AsyncButton considerava
    // sucesso e mostrava o toast verde sem nada ter sido criado.
    if (!pedidoId) { const e = new Error("Escolha o pedido."); setErro(e.message); throw e; }
    if (ambientes.length && !ambienteId) {
      const e = new Error(
        "Escolha o ambiente desta entrega — ele define a fatia do pedido no rateio.",
      );
      setErro(e.message); throw e;
    }
    if (!endereco.trim()) {
      const e = new Error("Endereço de entrega é obrigatório.");
      setErro(e.message); throw e;
    }
    const oe = await criarOe(request, {
      pedido_id: pedidoId,
      pedido_ambiente_id: ambienteId || undefined,
      endereco_entrega: endereco.trim(),
      telefone_contato: telefone.trim(),
      observacoes_entrega: observacoes.trim(),
    });
    limpar();
    if (onCriada) await onCriada(oe);
    if (onFechar) onFechar();
  };

  return (
    <SlideOverPanel
      open={aberto}
      title="Nova ordem de entrega"
      subtitle="A entrega nasce sozinha quando a produção carrega o ambiente — use isto para os casos avulsos."
      onClose={() => { limpar(); if (onFechar) onFechar(); }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {erro && <ErrorMsg>{erro}</ErrorMsg>}

        <Field>
          <span style={{ fontWeight: 600 }}>Pedido</span>
          <Select value={pedidoId} onChange={(e) => setPedidoId(e.target.value)}>
            <option value="">— selecione —</option>
            {pedidos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.numero} · {p.cliente_nome || "cliente"} ({p.situacao_display || p.situacao})
              </option>
            ))}
          </Select>
          <span className="tiny muted">
            Pedidos em produção ou expedição. Normalmente a OE nasce sozinha
            quando a produção carrega o ambiente.
          </span>
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Ambiente</span>
          <Select
            value={ambienteId}
            onChange={(e) => setAmbienteId(e.target.value)}
            disabled={!ambientes.length}
          >
            <option value="">
              {ambientes.length
                ? "— escolha o ambiente —"
                : "Pedido inteiro (sem ambientes cadastrados)"}
            </option>
            {ambientes.map((a) => (
              <option key={a.id} value={a.id}>{a.nome}</option>
            ))}
          </Select>
          <span className="tiny muted">
            Define a fatia do pedido que esta entrega representa — é sobre ela
            que a comissão e as parcelas da entrega são rateadas.
          </span>
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Endereço de entrega</span>
          <Input
            value={endereco}
            onChange={(e) => setEndereco(e.target.value)}
            placeholder="Rua, número, complemento — cidade/UF"
          />
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Telefone de contato</span>
          <Input
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="(11) 90000-0000"
          />
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Observações</span>
          <Textarea
            rows={3}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Referência, horário combinado, cuidados na descarga…"
          />
        </Field>

        <AsyncButton
          variant="primary"
          onClick={salvar}
          successMessage="Ordem de entrega criada."
          silentError
        >
          <FiTruck size={14} /> Criar OE
        </AsyncButton>
      </div>
    </SlideOverPanel>
  );
}
